// Copied verbatim from Training-Matrix-V2 app/src/lib/bookingDates.js (d5070a0).
// Keep the two in step: the main app and this app must describe a booking's
// dates the same way.

/**
 * Booking delivery window helpers.
 *
 * A booked training_quote carries the window the course actually runs over:
 *   booked_start_at  TIMESTAMPTZ  course start (date + time) — mirrors CRM bookings.start_date
 *   booked_end_at    TIMESTAMPTZ  course end   (date + time) — mirrors CRM bookings.end_date
 *   booked_day_release_dates  JSONB  non-contiguous day-release days
 *
 * `booked_date` (DATE) is the legacy single-date field. It is kept in sync with
 * booked_start_at by a DB trigger, so it still works — but it can only say when a course
 * BEGINS, never that it runs for three days. Read through these helpers instead, and they
 * degrade gracefully on rows that only ever had booked_date.
 *
 * Note: despite the name, `booked_date` is the date training is booked FOR. The date it was
 * booked ON is `booking_confirmed_date`.
 */

const LONDON = 'Europe/London';

/**
 * Training dates are UK dates. Read and render them in Europe/London explicitly rather than
 * in whatever zone the browser happens to be in — booked_start_at is a timestamptz, so a
 * course on 5 June is stored as 2026-06-04T23:00Z during BST and would render as 4 June to a
 * browser running on UTC. The legacy date-only booked_date never had that hazard, so getting
 * this wrong would be a regression.
 */
const londonYMD = (d) => d.toLocaleDateString('en-CA', { timeZone: LONDON }); // YYYY-MM-DD

/** A 'YYYY-MM-DD' as a Date at LOCAL noon — noon so no timezone can tip it to another day. */
const ymdToLocalNoon = (ymd) => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
};

/** Parse a value that may be a full ISO timestamp or a bare 'YYYY-MM-DD'. */
const parseLoose = (value) => {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value !== 'string') return null;
  // Date-only strings are already UK calendar dates; read them as local midnight so
  // `new Date('2026-02-24')` (which parses as UTC) can't render as the previous day.
  const iso = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The UK calendar day a stored value falls on, as a local-noon Date. */
const ukDayOf = (value) => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return ymdToLocalNoon(value);
  const d = parseLoose(value);
  return d ? ymdToLocalNoon(londonYMD(d)) : null;
};

/**
 * Course start as an instant. Falls back to the legacy single date.
 * For calendar-day questions prefer bookingDays() — this keeps the time of day.
 */
export const bookingStart = (booking) =>
  parseLoose(booking?.booked_start_at ?? booking?.booked_date ?? null);

/** Course end, or null when the booking is single-day / the end isn't known. */
export const bookingEnd = (booking) => parseLoose(booking?.booked_end_at ?? null);

/** The UK calendar day the course starts on, as a local-noon Date. */
const startDay = (booking) =>
  ukDayOf(booking?.booked_start_at ?? booking?.booked_date ?? null);

/** The UK calendar day the course ends on, or null. */
const endDay = (booking) => ukDayOf(booking?.booked_end_at ?? null);

const dayReleaseDays = (booking) => {
  const raw = booking?.booked_day_release_dates;
  if (!Array.isArray(raw)) return [];
  return raw.map(ukDayOf).filter(Boolean);
};

const keyOf = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

/**
 * Every distinct UK calendar day the course occupies — the start..end span plus any
 * day-release days. Ascending array of Dates at local noon.
 */
export const bookingDays = (booking) => {
  const start = startDay(booking);
  if (!start) return [];
  const end = endDay(booking);
  const seen = new Map();
  const push = (d) => {
    const k = keyOf(d);
    if (!seen.has(k)) seen.set(k, d);
  };

  push(start);
  if (end) {
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard < 400) {
      push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  }
  dayReleaseDays(booking).forEach(push);

  return [...seen.values()].sort((a, b) => a - b);
};

/** How many days the course runs over. 1 for a single-day or legacy date-only booking. */
export const bookingDayCount = (booking) => {
  const days = bookingDays(booking);
  return days.length || (startDay(booking) ? 1 : 0);
};

/** True when the booking spans more than one day. */
export const isMultiDayBooking = (booking) => bookingDayCount(booking) > 1;

/**
 * True when the window carries a meaningful time of day. Legacy rows (and CRM bookings
 * saved date-only) sit at UK midnight, where showing "00:00" would be a lie.
 */
export const hasBookingTimes = (booking) => {
  const meaningful = (v) => {
    const d = parseLoose(v);
    if (!d) return false;
    return d.toLocaleTimeString('en-GB', {
      timeZone: LONDON, hour12: false, hour: '2-digit', minute: '2-digit',
    }) !== '00:00';
  };
  // A legacy date-only value can never carry a time.
  return meaningful(booking?.booked_start_at) || meaningful(booking?.booked_end_at);
};

const fmtTime = (d) =>
  d.toLocaleTimeString('en-GB', { timeZone: LONDON, hour: '2-digit', minute: '2-digit' });

const fmtDay = (d, opts) => d.toLocaleDateString('en-GB', opts);

/**
 * The delivery window as text.
 *   single day     -> "24 Feb 2026"
 *   same month     -> "24 – 25 Feb 2026"
 *   same year      -> "24 Feb – 3 Mar 2026"
 *   spanning years -> "24 Feb 2026 – 3 Jan 2027"
 * Pass { weekday: true } to prefix weekday names.
 */
export const formatBookingDateRange = (booking, { weekday = false } = {}) => {
  const start = startDay(booking);
  if (!start) return null;
  const days = bookingDays(booking);
  const end = days.length > 1 ? days[days.length - 1] : null;

  const base = weekday ? { weekday: 'short' } : {};
  const full = { ...base, day: 'numeric', month: 'short', year: 'numeric' };

  if (!end) return fmtDay(start, full);

  if (start.getFullYear() === end.getFullYear()) {
    if (start.getMonth() === end.getMonth()) {
      return `${fmtDay(start, { ...base, day: 'numeric' })} – ${fmtDay(end, full)}`;
    }
    return `${fmtDay(start, { ...base, day: 'numeric', month: 'short' })} – ${fmtDay(end, full)}`;
  }
  return `${fmtDay(start, full)} – ${fmtDay(end, full)}`;
};

/** "09:00 – 16:30", or null when the booking carries no meaningful times. */
export const formatBookingTimeRange = (booking) => {
  if (!hasBookingTimes(booking)) return null;
  const start = parseLoose(booking?.booked_start_at);
  const end = parseLoose(booking?.booked_end_at);
  if (!start) return null;
  if (!end) return fmtTime(start);
  // On a multi-day course the times are the daily start/finish, not a continuous span.
  return `${fmtTime(start)} – ${fmtTime(end)}`;
};

/** "3 days" — or null for a single-day booking, where duration adds nothing. */
export const formatBookingDuration = (booking) => {
  const days = bookingDayCount(booking);
  return days > 1 ? `${days} days` : null;
};

/**
 * One-line summary for delegate-facing surfaces, e.g.
 *   "24 – 26 Feb 2026 · 3 days"
 */
export const formatBookingWhen = (booking, { weekday = false } = {}) => {
  const range = formatBookingDateRange(booking, { weekday });
  if (!range) return null;
  const duration = formatBookingDuration(booking);
  return duration ? `${range} · ${duration}` : range;
};

/** Columns any query needs in order to use these helpers. */
export const BOOKING_DATE_COLUMNS =
  'booked_date, booked_start_at, booked_end_at, booked_day_release_dates';
